import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { SubmitEvent, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AgreementBusinessModel, useProductCenterApi } from 'api';
import {
	agreementsBusinessModelsQuery,
	serviceContractTypesQuery,
} from 'api/queries';
import { agreementBusinessModelToTranslationMapping } from 'api/responses/models/Unit/ServiceAgreementDto';
import {
	DealerOwnedServiceContractType,
	VolvoOwnedServiceContractType,
	dealerOwnedServiceContractTypeToTranslationMapping,
	volvoOwnedServiceContractTypeToTranslationMapping,
} from 'api/responses/models/Unit/ServiceContractDto';
import { isCentralAdmin, isExternalUser, isSuperUser } from 'domain/user/User';
import { PageButtons } from 'library/components/controls/buttons/PageButtons';
import CustomSeverityDialog from 'library/components/controls/dialogs/CustomSeverityDialog';
import { ControlledDatePicker } from 'library/components/controls/form/ControlledDatePicker';
import { ControlledSelect } from 'library/components/controls/form/ControlledSelect';
import { ControlledSwitch } from 'library/components/controls/form/ControlledSwitch';
import { ControlledTextField } from 'library/components/controls/form/ControlledTextField';
import { PageFormContainer } from 'library/components/controls/pageTemplates/PageFormContainer';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';
import { BusinessPartnerPicker } from 'modules/Shared/Components/BusinessPartner/BusinessPartnerPicker';

import { mapServiceContractToServiceContractDto } from './mappers';
import { ServiceContract } from './types';

interface AddingManuallyServiceContractDetailsProps {
	onView: (serviceContract: ServiceContract) => void;
	onBackToList?: () => void;
	isEmob?: boolean;
}

interface AddingManuallyServiceContractFormData {
	startDate: DateTime | null;
	endDate: DateTime | null;
	startHour: string;
	endMaxHour: string;
	businessModel: string;
	serviceType: string;
	responsibleBusinessPartner: string | null;
	connectivityIncluded: boolean;
	preventiveRepair: boolean;
	validatedByVolvoPenta: boolean;
}

export const AddingManuallyServiceContractDetails = ({
	onView,
	onBackToList = noop,
	isEmob = false,
}: AddingManuallyServiceContractDetailsProps) => {
	const { t } = useTranslation([
		'serviceContracts',
		'common',
		'commonButton',
		'unitOverview',
	]);
	const { enqueueSnackbar } = useSnackbar();
	const { api } = useProductCenterApi();
	const [isCanceling, setIsCanceling] = useState(false);
	const user = useAppSelector(selectUser);

	const isExternal = useMemo(() => isExternalUser(user), [user]);
	const isSU = useMemo(() => isSuperUser(user), [user]);
	const isCA = useMemo(() => isCentralAdmin(user), [user]);
	const isValidatedByVolvo = useMemo(
		() => !isExternal && (isSU || isCA),
		[isExternal, isSU, isCA]
	);

	const businessModelsQuery = useQuery(agreementsBusinessModelsQuery(api));

	const { control, handleSubmit, watch } =
		useForm<AddingManuallyServiceContractFormData>({
			defaultValues: {
				startDate: null,
				endDate: null,
				startHour: '',
				endMaxHour: '',
				businessModel: isEmob ? '' : 'BM1',
				serviceType: '',
				responsibleBusinessPartner: isExternal
					? (user.customer?.customerName ?? null)
					: null,
				connectivityIncluded: false,
				preventiveRepair: false,
				validatedByVolvoPenta: false,
			},
		});

	const selectedBusinessModel = watch('businessModel');
	const serviceType = watch('serviceType');
	const isDealerOwnedBusinessModel = selectedBusinessModel === 'BM1';
	const isOtherServiceType = serviceType === 'Other';

	const serviceTypesQuery = useQuery({
		...serviceContractTypesQuery(api, selectedBusinessModel),
		enabled: !!selectedBusinessModel,
	});

	const handleFormSubmit = useCallback(
		async (data: AddingManuallyServiceContractFormData) => {
			if (data.startDate == null) {
				return;
			}

			const contract: ServiceContract = {
				status: 'Active', // initial status for new service contract
				startDate: data.startDate.toJSDate(),
				endDate: data.endDate?.toJSDate(),
				startHour:
					data.startHour !== '' ? Number(data.startHour) : undefined,
				endMaxHour:
					data.endMaxHour !== ''
						? Number(data.endMaxHour)
						: undefined,
				businessModel: data.businessModel || '',
				serviceType: data.serviceType || '',
				responsibleBusinessPartner:
					data.responsibleBusinessPartner ?? '',
				connectivityIncluded: data.connectivityIncluded,
				preventiveRepair: data.preventiveRepair,
				validatedByVolvoPenta: data.validatedByVolvoPenta,
				source: 'ManuallyAdded',
			};

			await api
				.addNewServiceContract(
					mapServiceContractToServiceContractDto(contract)
				)
				.then(() => {
					enqueueSnackbar(
						t('serviceContracts:adding-success-snackbar'),
						{
							variant: 'success',
						}
					);
					onView(contract);
				})
				.catch(() => {
					enqueueSnackbar(
						t('serviceContracts:adding-error-snackbar'),
						{
							variant: 'error',
						}
					);
					return;
				});
		},
		[enqueueSnackbar, onView, t]
	);

	const onSubmit = useCallback(
		(event: SubmitEvent<HTMLFormElement>) => {
			event.preventDefault();
			void handleSubmit(handleFormSubmit)(event);
		},
		[handleSubmit, handleFormSubmit]
	);

	const handleOnCancel = useCallback(() => {
		setIsCanceling(true);
	}, []);

	const handleOnAccept = useCallback(() => {
		setIsCanceling(false);
		onBackToList();
	}, [onBackToList]);

	const handleOnDecline = useCallback(() => {
		setIsCanceling(false);
	}, []);

	return (
		<PageFormContainer noValidate onSubmit={onSubmit}>
			<Typography variant="h6" sx={{ mx: 2, my: 4 }}>
				{t('serviceContracts:service-contracts')}
			</Typography>
			<Paper
				variant="flatGrey"
				sx={(theme) => ({
					p: 2,
					display: 'flex',
					gap: 10,
					[theme.breakpoints.down('sm')]: {
						flexDirection: 'column',
						gap: 2,
					},
					'.MuiInputBase-root.MuiOutlinedInput-root, .MuiPickersInputBase-root.MuiPickersOutlinedInput-root':
						{
							backgroundColor: 'background.paper',
						},
				})}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						flex: 1,
					}}>
					<ControlledDatePicker
						name="startDate"
						control={control}
						required
						fullWidth
						label={t('serviceContracts:start-date')}
						size="small"
						validation={{
							required: t('common:validation-error-message'),
						}}
					/>
					<ControlledDatePicker
						name="endDate"
						control={control}
						required
						fullWidth
						label={t('serviceContracts:end-date')}
						size="small"
						validation={{
							required: t('common:validation-error-message'),
							validate: (value, { startDate }) => {
								if (
									value != null &&
									startDate != null &&
									value < startDate
								) {
									return t(
										'serviceContracts:end-date-before-start-date'
									);
								}
								return true;
							},
						}}
					/>
					<ControlledTextField
						name="startHour"
						control={control}
						type="number"
						label={t('serviceContracts:start-hour')}
						size="small"
						fullWidth
					/>
					<ControlledTextField
						name="endMaxHour"
						control={control}
						type="number"
						label={t('serviceContracts:end-max-hour')}
						size="small"
						fullWidth
					/>
					{isEmob ? (
						<ControlledSelect
							name="businessModel"
							control={control}
							label={t('serviceContracts:business-model')}
							options={
								businessModelsQuery.data?.map((d) => ({
									id: d.label as AgreementBusinessModel,
									label: t(
										agreementBusinessModelToTranslationMapping[
											d.label as AgreementBusinessModel
										]
									),
								})) ?? []
							}
							fullWidth
							size="small"
							validation={{
								required: t('common:validation-error-message'),
							}}
							isLoading={businessModelsQuery.isLoading}
						/>
					) : (
						<Typography sx={{ ml: 0.5 }}>
							{t('serviceContracts:business-model-pre-defined')}
						</Typography>
					)}
					<ControlledSelect
						name="serviceType"
						control={control}
						label={t('serviceContracts:service-contract-type')}
						options={
							serviceTypesQuery.data?.map((d) => ({
								id: d.id,
								label: t(
									isDealerOwnedBusinessModel
										? dealerOwnedServiceContractTypeToTranslationMapping[
												d.label as DealerOwnedServiceContractType
											]
										: volvoOwnedServiceContractTypeToTranslationMapping[
												d.label as VolvoOwnedServiceContractType
											]
								),
							})) ?? []
						}
						fullWidth
						size="small"
						validation={{
							required: t('common:validation-error-message'),
						}}
						isLoading={serviceTypesQuery.isLoading}
						disabled={!selectedBusinessModel}
					/>
					{isExternal ? (
						<Typography sx={{ ml: 0.5 }}>
							{t(
								'serviceContracts:responsible-business-partner-value',
								{
									businessPartner:
										user.customer?.customerName ?? '',
								}
							)}
						</Typography>
					) : (
						<BusinessPartnerPicker<AddingManuallyServiceContractFormData>
							id="service-contract-business-partner"
							name="responsibleBusinessPartner"
							control={control}
							label={t(
								'serviceContracts:responsible-business-partner'
							)}
							size="small"
							validation={{
								required: t('common:validation-error-message'),
							}}
						/>
					)}
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 3,
						flex: 1,
					}}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 0.5,
						}}>
						<ControlledSwitch
							name="connectivityIncluded"
							control={control}
							label={t('serviceContracts:connectivity-included')}
						/>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								ml: 6,
							}}>
							{t(
								'serviceContracts:connectivity-included-description'
							)}
						</Typography>
					</Box>
					{isOtherServiceType && (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 0.5,
							}}>
							<ControlledSwitch
								name="preventiveRepair"
								control={control}
								label={t('serviceContracts:preventive-repair')}
							/>
							<Typography
								variant="body2"
								sx={{
									color: 'text.secondary',
									ml: 6,
								}}>
								{t(
									'serviceContracts:preventive-repair-description'
								)}
							</Typography>
						</Box>
					)}
					{isDealerOwnedBusinessModel && isValidatedByVolvo && (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 0.5,
							}}>
							<ControlledSwitch
								name="validatedByVolvoPenta"
								control={control}
								label={t(
									'serviceContracts:validated-by-volvo-penta'
								)}
							/>
							<Typography
								variant="body2"
								sx={{
									color: 'text.secondary',
									ml: 6,
								}}>
								{t(
									'serviceContracts:validated-by-volvo-penta-description'
								)}
							</Typography>
						</Box>
					)}
				</Box>
			</Paper>
			<PageButtons
				secondaryButtonLabel={t('commonButton:cancel')}
				primaryButtonLabel={t('commonButton:add')}
				isPrimaryButtonSubmitType
				withDivider={false}
				secondaryButtonAction={handleOnCancel}
			/>
			<CustomSeverityDialog
				headerText={t('serviceContracts:adding-cancel-dialog-title')}
				content={t('serviceContracts:adding-cancel-dialog-description')}
				onDecline={handleOnDecline}
				onAccept={handleOnAccept}
				acceptButtonLabel={t('commonButton:yes')}
				declineButtonLabel={t('commonButton:no')}
				isOpen={isCanceling}
			/>
		</PageFormContainer>
	);
};
